import { InputWithLabel } from '@adotel/components'
import { useLazyQuery, useMutation } from '@apollo/client'
import {
  AppBar,
  Button,
  CircularProgress,
  Typography,
  useMediaQuery,
  useTheme
} from '@material-ui/core'
import AddIcon from '@material-ui/icons/Add'
import PolicyIcon from '@material-ui/icons/Policy'
import { Form, Formik } from 'formik'
import { useEffect, useMemo, useState } from 'react'
import { useHistory, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'

import Breadcrumbs from '../../components/Breadcrumb'
import PageHeader from '../../components/PageHeader'
import SectionTitle from '../../components/SectionTitle'
import {
  ADD_ON_POLICY_GENERATE,
  ADD_ON_POLICY_MODIFY,
  ADD_ON_POLICY_QUERY
} from '../../graphql/addOnPolicies'
import apolloClient from '../../graphql/apolloClient'
import { getTranslate, lang } from '../../localization'
import { AddOnPolicy, PolicyTable } from '../../types/cancelAndModifyPolicies'
import { Multilingual } from '../../types/roomtype'
import PoliciesFormAddPoliciesModal from '../PoliciesForm/PoliciesFormAddPoliciesModal'
import PoliciesFormAvailablePoliciesList from '../PoliciesForm/PoliciesFormAvailablePoliciesList'
import PoliciesFormInputBox from '../PoliciesForm/PoliciesFormInputBox'
import { sortPolicies } from '../PoliciesForm/policiesSorter'
import useInputBoxStyles from '../PoliciesForm/styles/PoliciesFormInputBox.styles'
import useStyles from '../PoliciesForm/styles/PoliciesFormMain.styles'
import { AddOnPolicyFormValidationSchema } from './validations'

const ADD_ON_POLICY_FORM_INIT_VALUES: AddOnPolicy = {
  basicDetails: {
    name: '',
    description: '',
    descriptionTranslatedList: [
      { label: 'Iran, Islamic Republic of', value: '', nativeLabel: 'فارسی' }
    ],
    id: ''
  },
  cutoffHours: 0,
  table: []
}

// Add-on rules price a removal on the add-on's whole amount; First Night has no meaning for an add-on.
const ADD_ON_PENALTY_TYPES: PolicyTable['penaltyType'][] = ['TotalStay']

const AddOnPoliciesForm = () => {
  const classes = useStyles()
  const inputBoxClasses = useInputBoxStyles()
  const history = useHistory()
  const theme = useTheme()
  const matches = useMediaQuery(theme.breakpoints.down('sm'))
  const { providerCode, id } = useParams<{
    providerCode: string
    id: string
  }>()

  const [getInitialValues, { loading: loadingForData }] = useLazyQuery<{
    addOnPolicy: AddOnPolicy
  }>(ADD_ON_POLICY_QUERY, {
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'network-only'
  })

  const [submitAddOnPolicy, { loading: loadingForSubmit }] = useMutation(
    id ? ADD_ON_POLICY_MODIFY : ADD_ON_POLICY_GENERATE,
    { notifyOnNetworkStatusChange: true }
  )

  const [descriptionMultiLingual, setDescriptionMultiLingual] = useState<
    Multilingual[]
  >([])
  const [initialValues, setInitialValues] = useState<AddOnPolicy>(
    ADD_ON_POLICY_FORM_INIT_VALUES
  )
  const [providerCodeState, setProviderCodeState] =
    useState<string>(providerCode)
  const [policiesModalIsOpen, setPoliciesModalIsOpen] = useState(false)

  useEffect(() => {
    if (!id) return
    getInitialValues({
      variables: { providerCode: providerCodeState, policyID: id }
    }).then(({ data }) => {
      const loaded = data?.addOnPolicy
      if (!loaded) return
      setInitialValues(loaded)
      if (loaded.basicDetails.descriptionTranslatedList.length > 0) {
        setDescriptionMultiLingual(
          loaded.basicDetails.descriptionTranslatedList
        )
      }
    })
  }, [])

  const handleSubmit = async (values: AddOnPolicy) => {
    const table: PolicyTable[] = values.table.map((row) => ({
      deadline: row.deadline,
      deadlineTimeOffset: row.deadlineTimeOffset,
      penaltyAmount: row.penaltyAmount,
      penaltyType: row.penaltyType,
      policyType: row.policyType,
      priority: row.priority
    }))

    try {
      const { data: response } = await submitAddOnPolicy({
        variables: {
          input: {
            table,
            cutoffHours: Number(values.cutoffHours),
            basicDetails: {
              ...values.basicDetails,
              descriptionTranslatedList: descriptionMultiLingual
            }
          },
          providerCode: providerCode ? providerCode : providerCodeState
        }
      })

      toast.success(
        id
          ? response.modifyAddOnPolicy.message
          : response.generateAddOnPolicy.message
      )
      apolloClient.cache.evict({ fieldName: 'addOnPolicies' })
      history.push(`/${lang}/policies/add-on-policies`)
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'ERROR!')
    }
  }

  const cachedLevels = useMemo(
    () => [
      {
        label: getTranslate('Add-On Policies'),
        link: '/policies/add-on-policies'
      },
      { label: initialValues.basicDetails.name }
    ],
    [initialValues]
  )

  return (
    <div className={classes.root}>
      <PageHeader title={getTranslate('Add-On Policies')} />
      {id && (
        <AppBar
          className={classes.appBar}
          position="static"
          color="transparent">
          <Breadcrumbs levels={cachedLevels} loading={loadingForData} />
        </AppBar>
      )}
      <div className={classes.contentContainer}>
        <div className={classes.header}>
          <PolicyIcon fontSize="large" />
          <Typography className={classes.title} variant="h3" gutterBottom>
            {id
              ? initialValues.basicDetails.name
              : getTranslate('Add New Policies')}
          </Typography>
        </div>
        <Formik
          enableReinitialize
          initialValues={initialValues}
          onSubmit={handleSubmit}
          validationSchema={AddOnPolicyFormValidationSchema}>
          {({
            values,
            setFieldValue,
            handleBlur,
            isSubmitting,
            errors,
            touched,
            isValid
          }) => (
            <Form>
              <div className={classes.titleContainer}>
                <SectionTitle title="Basic Details" marginBottom />
                {loadingForData ? <CircularProgress /> : null}
              </div>
              <PoliciesFormInputBox
                {...{
                  values,
                  setFieldValue,
                  handleBlur,
                  isSubmitting,
                  errors,
                  touched,
                  classes,
                  loadingForData
                }}
                providerCodeState={providerCodeState}
                setProviderCodeState={setProviderCodeState}
                descriptionMultiLingual={descriptionMultiLingual}
                setDescriptionMultiLingual={setDescriptionMultiLingual}
              />
              <div className={classes.titleContainer}>
                <SectionTitle title="Cut-off" marginBottom />
              </div>
              <div className={inputBoxClasses.textEditorContainer}>
                <div className={inputBoxClasses.input}>
                  <InputWithLabel
                    name="cutoffHours"
                    type="number"
                    label={getTranslate('Hours before check-in')}
                    fullWidth
                    inputSize="small"
                    value={values.cutoffHours}
                    onChange={(event: { currentTarget: { value: string } }) =>
                      setFieldValue(
                        'cutoffHours',
                        event.currentTarget.value === ''
                          ? ''
                          : Number(event.currentTarget.value)
                      )
                    }
                    error={
                      (touched.cutoffHours && !!errors.cutoffHours) || undefined
                    }
                    helpertext={
                      touched.cutoffHours && errors.cutoffHours
                        ? getTranslate(errors.cutoffHours)
                        : getTranslate(
                            'Guests can add, remove or swap this add-on until this many hours before check-in'
                          )
                    }
                    onBlur={handleBlur}
                    disabled={isSubmitting}
                  />
                </div>
              </div>
              <div className={classes.tableHeaderContainer}>
                <SectionTitle title="Policies" marginBottom />
                <Button
                  variant="contained"
                  color="primary"
                  className={classes.button}
                  size="medium"
                  fullWidth={matches}
                  startIcon={<AddIcon />}
                  onClick={() => setPoliciesModalIsOpen(true)}>
                  {getTranslate('Add Policies')}
                </Button>
              </div>
              <PoliciesFormAddPoliciesModal
                values={values}
                setFieldValue={setFieldValue}
                penaltyTypes={ADD_ON_PENALTY_TYPES}
                isOpen={policiesModalIsOpen}
                onClose={() => setPoliciesModalIsOpen(false)}
              />
              <div>
                {values.table.length > 0 ? (
                  <PoliciesFormAvailablePoliciesList
                    onDelete={(row: PolicyTable) =>
                      setFieldValue(
                        'table',
                        values.table.filter((item) => item !== row)
                      )
                    }
                    policiesData={sortPolicies(values.table)}
                  />
                ) : (
                  <Typography variant="h4" className={classes.noPilocies}>
                    {getTranslate(
                      'No rules: removing the add-on is free until the cut-off'
                    )}
                  </Typography>
                )}
              </div>
              <div className={classes.footer}>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  disabled={!isValid || loadingForSubmit}
                  size="medium"
                  fullWidth={matches}>
                  {getTranslate('Save Policies')}
                  {loadingForSubmit && (
                    <CircularProgress
                      className={classes.circularProgress}
                      size={16}
                    />
                  )}
                </Button>
              </div>
            </Form>
          )}
        </Formik>
      </div>
    </div>
  )
}

export default AddOnPoliciesForm
